import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { refreshAdminAccessToken, registerAdminOAuthClientId } from '@gto/api-client'
import { logSentry } from '@gto/sentry'
import { sessionStore } from '@gto/session'

import { getOAuthClientId } from '@/utils/oauth'

import { App } from './app'

// Hand the admin DOAT client the per-env OAuth client_id. Must run before
// the first call to `refreshAdminAccessToken` (bootstrap) or any admin API
// hook (which 401s would trigger a refresh) — the client throws loudly if
// it has no client_id when a refresh is attempted.
registerAdminOAuthClientId(getOAuthClientId)

// Auto-reload on stale-chunk 404. After a Cloudflare Pages deploy the
// previously-loaded `index.html` references hashed bundles that no longer
// exist; the next route transition tries to `import('route.lazy-<oldHash>.js')`
// and throws. Vite emits `vite:preloadError` for this case — reload so the
// tab picks up the fresh `index.html` and the new hashes.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', () => {
    window.location.reload()
  })
}

// Grab #root SYNCHRONOUSLY at module-eval time. If it's missing the throw
// surfaces immediately as a script-level error (visible in the console
// + Sentry's global handler); deferring this to the async `bootstrap()`
// body would turn it into a swallowed unhandled-rejection.
const rootElement: HTMLElement | null = document.getElementById('root')
if (rootElement === null) {
  throw new Error('admin-web: #root element missing — index.html or HTML transform regression')
}
const ROOT: HTMLElement = rootElement

// Bootstrap auth BEFORE the router renders, so the first `beforeLoad` pass
// in `_layout` sees the access token in sessionStore. A returning user has
// their refresh DOAT in localStorage from a previous OAuth sign-in on this
// origin — mint a fresh access token from it so a hard refresh / pasted
// URL / tab restore doesn't bounce them to /login. If refresh fails
// (token expired / revoked / rotated by another tab), fall through and the
// `_layout` gate handles the redirect.
async function bootstrap() {
  if (
    sessionStore.getState().accessToken === null &&
    localStorage.getItem('user_refresh') !== null
  ) {
    try {
      await refreshAdminAccessToken()
    } catch (e) {
      // Always drop the refresh on bootstrap failure — keeping a known-bad
      // refresh would loop bootstrap forever on every subsequent reload
      // with no UI recovery path. The user then goes through /login
      // cleanly. Sentry-logged so the failure category stays observable.
      logSentry('admin-web: bootstrap refreshAdminAccessToken failed', e)
      localStorage.removeItem('user_refresh')
    }
  }

  const root = createRoot(ROOT)
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

void bootstrap()
